import { AlertTriangle } from 'lucide-react';
import { ChannelIcon } from '../../components/ui/ChannelIcon';
import { cn } from '../../lib/utils';
import { destinationById, type PostDestinationId } from '../../lib/publishing/destinations';
import { summarizeCoverage, type AccountCoverage } from '../../lib/publishing/multiAccount';

// Publishing multi-account — the coverage preview (2026-07-28, Scott's safety
// check). For every selected account it shows the destinations it WILL post to
// (ticked ∩ connected) as ChannelIcon glyphs, the ticked-but-not-connected ones
// greyed (skipped for that account), and flags a ZERO-coverage account
// prominently — it would receive nothing and is not created. "Show and skip
// missing destinations" made visible.

// One destination glyph: the platform icon + a Story badge when it's a story
// placement (mirrors the caption counter row / options card).
export function DestinationChip({ dest, muted }: { dest: PostDestinationId; muted?: boolean }) {
  const d = destinationById(dest);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px]',
        muted ? 'text-slate-300 line-through' : 'text-slate-600',
      )}
      title={muted ? `${d.label} — not connected, skipped` : d.label}
    >
      <ChannelIcon channel={d.platform} className="h-3.5 w-3.5" />
      {d.placement === 'story' && (
        <span className="rounded bg-slate-100 px-0.5 text-[8px] font-semibold uppercase tracking-wide">
          Story
        </span>
      )}
    </span>
  );
}

export function CoveragePanel({ plan }: { plan: AccountCoverage[] }) {
  if (plan.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">
        Select accounts to preview coverage.
      </div>
    );
  }

  const summary = summarizeCoverage(plan);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">Coverage</span>
        <span className="text-xs text-slate-500">
          {summary.willReceive} of {summary.totalSelected} will receive
          {summary.zeroCoverage > 0 && (
            <span className="ml-1 font-medium text-amber-700">
              · {summary.zeroCoverage} none
            </span>
          )}
        </span>
      </div>

      <ul className="scroll-subtle max-h-[24rem] space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
        {plan.map((row) => (
          <li
            key={row.account.id}
            className={cn(
              'flex items-center gap-2 rounded px-2 py-1.5',
              row.hasCoverage ? 'hover:bg-slate-50' : 'border border-amber-200 bg-amber-50',
            )}
          >
            <span className="min-w-0 flex-1 truncate text-sm text-slate-900">{row.account.name}</span>
            {row.hasCoverage ? (
              <span className="flex shrink-0 items-center gap-1">
                {row.covered.map((d) => (
                  <DestinationChip key={`c-${d}`} dest={d} />
                ))}
                {row.skipped.map((d) => (
                  <DestinationChip key={`s-${d}`} dest={d} muted />
                ))}
              </span>
            ) : (
              <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-amber-700">
                <AlertTriangle className="h-3.5 w-3.5" />
                Receives nothing
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
